<template>
  <div>
    <component :is="entityClass" @filterChanged="filterChanged" />
    <br />
    <!-- Tags-->
    <div class="columns">
      <div class="column">
        <l-field
          :label="$t('compTransitionMapsFilterItems.field-tags')"
          :tooltip="$t('compTransitionMapsFilterItems.tooltip-tags')"
          label-position="on-border"
        >
          <b-taginput
            :allow-duplicate="false"
            ellipsis
            :data="filteredTags"
            autocomplete
            :allow-new="false"
            :open-on-focus="true"
            icon="tags"
            aria-close-label="Delete this tag"
            ref="taginput"
            :placeholder="$t('compTransitionMapsFilterItems.search-tags')"
            @input="tagsChanged"
            @typing="getFilteredTags"
          >
            >
            <!-- suggested items -->
            <template slot-scope="props">
              <action-tag
                :tag="props.option"
                :tooltipPosition="'is-right'"
                :tooltipMultilined="false"
              ></action-tag>
            </template>
            <!-- selected items -->
            <template #selected="props">
              <action-tag
                v-for="(tag, index) in props.tags"
                :key="index"
                :tag="tag"
                closable
                @close="$refs.taginput.removeTag(index, $event)"
              >
              </action-tag>
            </template>
          </b-taginput>
        </l-field>
      </div>
      <div class="column is-one-third">
        <div class="block">
          <l-field
            label=""
            :tooltip="$t('compTransitionMapsFilterItems.tooltip-tags-op')"
            label-position="on-border"
          >
            <b-radio-button
              @input="tagsOpChanged"
              v-model="filters.tagsOp"
              size="is-small"
              native-value="AND"
            >
              {{ $t('compTransitionMapsFilterItems.filter:and') }}
            </b-radio-button>
            <b-radio-button
              @input="tagsOpChanged"
              v-model="filters.tagsOp"
              size="is-small"
              native-value="OR"
            >
              {{ $t('compTransitionMapsFilterItems.filter:or') }}
            </b-radio-button>
          </l-field>
        </div>
      </div>
    </div>

    <l-field
      :label="$t('compTransitionMapsFilterItems.field-title')"
      :tooltip="$t('compTransitionMapsFilterItems.tooltip-title')"
      label-position="on-border"
    >
      <b-input
        @input="titleChanged"
        :placeholder="$t('compTransitionMapsFilterItems.placeholder-title')"
      ></b-input>
    </l-field>

    <!-- lab-->
    <l-field
      :label="$t('compTransitionMapsFilterItems.field-labs')"
      :tooltip="$t('compTransitionMapsFilterItems.tooltip-labs')"
      label-position="on-border"
    >
      <b-autocomplete
        :data="suggestedLabs"
        :placeholder="$t('compTransitionMapsFilterItems.placeholder-labs')"
        :loading="labIsFetching"
        clearable
        field="name"
        @typing="getEntities"
        @select="entitiesChanged"
        @input="entitiesInputChanged"
      >
        <template slot-scope="props">
          {{ props.option.name }}
        </template>
      </b-autocomplete>
    </l-field>

    <div class="columns is-multiline">
      <div class="column">
        <!-- sort -->
        <b-field
          :label="$t('compTransitionMapsFilterItems.field-sort')"
          label-position="on-border"
        >
          <b-select
            size="is-small"
            v-model="filters.sort"
            :placeholder="$t('compTransitionMapsFilterItems.' + filters.sort)"
            @input="sortChanged"
          >
            <option value="sort:update:desc">
              {{ $t('compTransitionMapsFilterItems.sort:update:desc') }}
            </option>
            <option value="sort:update:asc">
              {{ $t('compTransitionMapsFilterItems.sort:update:asc') }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="column">
        <b-field
          :label="$t('compTransitionMapsFilterItems.field-status')"
          label-position="on-border"
        >
          <b-select
            size="is-small"
            v-model="filters.status"
            :placeholder="$t('compTransitionMapsFilterItems.' + filters.status)"
            @input="statusChanged"
          >
            <option value="status:running">
              {{ $t('compTransitionMapsFilterItems.status:running') }}
            </option>
            <option value="status:ended">
              {{ $t('compTransitionMapsFilterItems.status:ended') }}
            </option>
            <option value="status:abandoned">
              {{ $t('compTransitionMapsFilterItems.status:abandoned') }}
            </option>
            <option value="status:nostatus">
              {{ $t('compTransitionMapsFilterItems.status:nostatus') }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
  </div>
</template>

<script>
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

import { Background } from '@/mixins/background.js'
import transitionService from '@/services/transitionService'
import { TAGS } from '@/models/transition/transition.js'
import _ from 'lodash'

import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'FilterItems',
  components: {
    'action-tag': () => import('@/components/transition/ActionTag.vue'),
    'administration-input': () =>
      import('@/components/core/AdministrationInput.vue'),
    'l-field': () => import('@/components/LField.vue'),
    treeselect: () => import('@riophae/vue-treeselect'),
    Laboratory: () => import('./FiltersItems/Laboratory.vue'),
    Entity: () => import('./FiltersItems/Entity.vue'),
    Campus: () => import('./FiltersItems/Campus.vue')
  },
  mixins: [Background],
  props: {},
  data: function () {
    return {
      availableTags: TAGS,
      filteredTags: TAGS,
      // zoom: 5,
      // center: [46.75, 2.32],
      // hold the parameters of the avail. filters
      // FIXME(msimonin): use a prop that initializes the filters to a default
      // value and display such value in the filter fields
      filters: {
        disciplines: [],
        disciplinesOp: 'AND',
        administrations: [],
        administrationsOp: 'AND',
        sort: 'sort:update:desc',
        status: 'status:nostatus',
        tags: [],
        tagsOp: 'AND',
        title: ''
      },
      actions: [],
      disciplinesTree: [],
      suggestedLabs: [],
      labIsFetching: false
    }
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  computed: {
    entityClass: function () {
      return this.coreStore.entityClass
    }
  },
  methods: {
    getEntities: _.debounce(function (name) {
      if (!name.length) {
        this.suggestedLabs = []
        return
      }
      this.labIsFetching = true
      transitionService
        .getEntities(name)
        .then((data) => {
          this.suggestedLabs = []
          data.forEach((item) => this.suggestedLabs.push(item))
        })
        .catch((error) => {
          this.suggestedLabs = []
          throw error
        })
        .finally(() => {
          this.labIsFetching = false
        })
    }, 500),
    filtered () {
      this.$emit('filtered', this.filters)
    },
    entitiesInputChanged (entityName) {
      if (!entityName) {
        // reset filter
        this.filters.entityId = null
        this.filtered()
      }
    },
    entitiesChanged: _.debounce(function (entity) {
      if (entity) {
        this.filters.entityId = entity.id
        this.filtered()
      }
    }, 500),
    titleChanged: _.debounce(function (title) {
      this.filters.title = title
      this.filtered()
    }, 500),
    tagsOpChanged (op) {
      this.filters.tagsOp = op
      this.filtered()
    },
    tagsChanged (tags) {
      this.filters.tags = tags
      this.filtered()
    },
    filterChanged (filter) {
      Object.assign(this.filters, filter)
      this.filtered()
    },
    statusChanged () {
      this.filtered()
    },
    sortChanged (a) {
      this.filtered()
    },
    getFilteredTags (text) {
      // support autocompletion for several lang
      this.filteredTags = this.availableTags.filter((tag) => tag.match(text))
    },
    /**
     *
     * Search function triggered when adding a tag on the search filter
     * We filter based on the descriptor tag which is locale agnostic
     * @param {Plan} plan
     * @param {Tag[]} input
     */
    searchTags (actionLab, input) {
      let planDescriptors = actionLab.action.tags.map((t) => t.descriptor)
      let inputDescriptors = input.map((t) => t.descriptor)
      let intersection = _.intersection(planDescriptors, inputDescriptors)
      return intersection.length === inputDescriptors.length
    }
  }
}
</script>
